import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartPulseIcon, UsersIcon, SparklesIcon, TrendingUpIcon, LeafIcon, ActivityIcon, QuoteIcon, DumbbellIcon, WavesIcon, MoonIcon, SunIcon } from 'lucide-react';
import { Logo, Button, Section, FadeIn } from '../ui';
import { SiteFooter } from '../SiteFooter';
import { BeforeAfter } from '../BeforeAfter';
export function ForHealthClubsPage() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const wellnessPartners = [{
    name: 'Huberman Lab',
    category: 'Neuroscience & Performance'
  }, {
    name: 'Peter Attia MD',
    category: 'Longevity Medicine'
  }, {
    name: 'Mayo Clinic',
    category: 'Clinical Wellness'
  }, {
    name: 'Hintsa Performance',
    category: 'Executive Wellbeing'
  }, {
    name: 'Whoop',
    category: 'Recovery Science'
  }, {
    name: 'Headspace',
    category: 'Mindfulness'
  }, {
    name: 'Oura Ring',
    category: 'Sleep & Readiness'
  }, {
    name: 'NASM',
    category: 'Movement Science'
  }];
  return <div className="min-h-screen bg-white font-sans text-slate-800" data-id="element-3255">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-3256">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-3257">
          <Link to="/" data-id="element-3258">
            <Logo data-id="element-3259" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-id="element-3260">
            <Button variant="primary" href="/designer" data-id="element-3261">
              Design a Member Retreat
            </Button>
          </nav>
        </div>
      </header>

      <main data-id="element-3262">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden" data-id="element-3263">
          <div className="absolute inset-0 pointer-events-none" data-id="element-3264">
            <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-brand-teal/[0.04] rounded-full blur-3xl" data-id="element-3265" />
            <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-emerald-200/[0.15] rounded-full blur-3xl" data-id="element-3266" />
          </div>

          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-3267">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-3268">
              <div data-id="element-3269">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5
              }} data-id="element-3270">
                  <div className="inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-6" data-id="element-3271">
                    <HeartPulseIcon className="w-4 h-4" data-id="element-3272" />
                    For Health Clubs & Spas
                  </div>
                  <h1 className="text-4xl md:text-6xl font-bold text-brand-navy leading-[1.1] mb-6 tracking-tight" data-id="element-3273">
                    Programmed wellness experiences your{' '}
                    <span className="text-brand-teal" data-id="element-3274">members crave.</span>
                  </h1>
                  <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-3275">
                    Turn your gym, spa, or wellness club into a destination for
                    signature group experiences — recovery retreats, longevity
                    workshops, mobility intensives, sleep & nutrition labs.
                    Powered by world-class wellness science.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4" data-id="element-3276">
                    <Button variant="primary" size="lg" href="/designer" className="gap-2" data-id="element-3277">
                      <SparklesIcon className="w-5 h-5" data-id="element-3278" />
                      Design a Retreat
                    </Button>
                    <Button variant="secondary" size="lg" href="#playbook" data-id="element-3279">
                      See the Playbook
                    </Button>
                  </div>
                </motion.div>
              </div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="relative" data-id="element-3280">
                <div className="absolute inset-0 bg-gradient-to-tr from-brand-teal/20 to-emerald-300/20 rounded-[2.5rem] transform rotate-3 scale-105 blur-lg opacity-50" data-id="element-3281" />
                <div className="relative bg-white rounded-[2rem] border border-slate-100 shadow-2xl p-8" data-id="element-3282">
                  <div className="flex items-center justify-between mb-8" data-id="element-3283">
                    <div data-id="element-3284">
                      <div className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-3285">
                        Live Member Retreat
                      </div>
                      <div className="text-xl font-bold text-brand-navy" data-id="element-3286">
                        Recovery & Longevity Day
                      </div>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal" data-id="element-3287">
                      <HeartPulseIcon className="w-6 h-6" data-id="element-3288" />
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-3289">
                    {[{
                    time: '07:30 AM',
                    title: 'Breathwork & Cold Plunge Circuit',
                    type: 'Recovery'
                  }, {
                    time: '09:30 AM',
                    title: 'Longevity Lab — VO₂ Max Testing',
                    type: 'Science'
                  }, {
                    time: '02:00 PM',
                    title: 'Mobility & Movement Reset',
                    type: 'Training'
                  }, {
                    time: '05:30 PM',
                    title: 'Sauna + Sound Bath Wind-down',
                    type: 'Restorative'
                  }].map((item, i) => <div key={i} className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100" data-id="element-3290">
                        <div className="text-sm font-bold text-slate-400 w-20 shrink-0 pt-0.5" data-id="element-3291">
                          {item.time}
                        </div>
                        <div data-id="element-3292">
                          <div className="font-bold text-brand-navy mb-1" data-id="element-3293">
                            {item.title}
                          </div>
                          <div className="text-xs font-semibold text-brand-teal uppercase tracking-wider" data-id="element-3294">
                            {item.type}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Band */}
        <section className="bg-brand-navy py-12" data-id="element-3295">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-3296">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-3297">
              {[{
              value: '3.2x',
              label: 'Avg revenue per member day'
            }, {
              value: '+48%',
              label: 'Member retention uplift'
            }, {
              value: '120+',
              label: 'Wellness programs ready to run'
            }, {
              value: '0',
              label: 'New staff required'
            }].map((stat, i) => <div key={i} className="text-center md:border-l md:border-white/10 first:border-l-0 md:px-4" data-id="element-3298">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-3299">
                    {stat.value}
                  </div>
                  <div className="text-sm text-brand-teal font-medium" data-id="element-3300">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Experience Pillars */}
        <Section className="bg-white" data-id="element-3301">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3302">
            <FadeIn data-id="element-3303">
              <div className="text-xs font-bold text-brand-teal uppercase tracking-[0.2em] mb-4" data-id="element-3304">
                The Experiences
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4" data-id="element-3305">
                Built for the body, mind, and recovery.
              </h2>
              <p className="text-lg text-slate-600" data-id="element-3306">
                Pre-built group experience templates designed by wellness
                practitioners — drag, drop, and run.
              </p>
            </FadeIn>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3307">
            {[{
            icon: DumbbellIcon,
            title: 'Movement & Strength',
            items: ['Mobility intensives', 'Group strength labs', 'Zone-2 training']
          }, {
            icon: WavesIcon,
            title: 'Recovery & Spa',
            items: ['Sauna + ice protocols', 'Massage rotations', 'Lymphatic circuits']
          }, {
            icon: MoonIcon,
            title: 'Sleep & Mind',
            items: ['Breathwork sessions', 'Sound healing', 'Sleep & HRV workshops']
          }, {
            icon: SunIcon,
            title: 'Nutrition & Longevity',
            items: ['Metabolic labs', 'VO₂ max testing', 'Chef-led nutrition']
          }].map((pillar, i) => {
            const Icon = pillar.icon;
            return <FadeIn key={i} delay={i * 0.08} data-id="element-3308">
                  <div className="bg-slate-50 rounded-2xl p-6 h-full border border-slate-100" data-id="element-3309">
                    <div className="w-12 h-12 rounded-xl bg-white text-brand-teal flex items-center justify-center mb-5 shadow-sm" data-id="element-3310">
                      <Icon className="w-6 h-6" data-id="element-3311" />
                    </div>
                    <h3 className="font-bold text-brand-navy mb-3" data-id="element-3312">
                      {pillar.title}
                    </h3>
                    <ul className="space-y-2" data-id="element-3313">
                      {pillar.items.map((item, j) => <li key={j} className="text-sm text-slate-600 flex items-start gap-2" data-id="element-3314">
                          <span className="w-1 h-1 rounded-full bg-brand-teal mt-2 shrink-0" data-id="element-3315" />
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </FadeIn>;
          })}
          </div>
        </Section>

        <BeforeAfter eyebrow="The Wellness Club Difference" headline={<>
              The same members. Two completely{' '}
              <span className="text-brand-coral" data-id="element-3317">different experiences</span>.
            </>} subhead="Offsite+ turns hotel-banquet wellness events into branded, member-results-driven retreats — with a live experience hub, real-time results, and club-growth analytics." image="https://cdn.magicpatterns.com/uploads/o6LTpxVHTLPmUjPo9ozMDC/Offsite_Plus_Health_Club.webp" alt="Before and after for a health club: dim ballroom keynote and crowded buffet versus a wellness-club Offsite+ event with live results dashboard, experience hub, and engaged member groups in an outdoor wellness setting" beforeItems={['Dim ballroom keynote with no member context', 'Buffet line and tense small talk', 'No visibility into who showed up or engaged', 'Survey card no one fills out']} afterItems={['Live Wellness Club dashboard — engagement, retention, growth', 'Experience hub: schedule, recovery, classes, nutrition, polling', 'Small-group coaching pods around real member goals', 'Member-results report the GM can act on Monday morning']} afterLabel="After · with Offsite+" data-id="element-3316" />

        {/* Playbook Section */}
        <Section id="playbook" className="bg-slate-50" data-id="element-3318">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3319">
            <FadeIn data-id="element-3320">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4" data-id="element-3321">
                The Wellness Operator's Playbook
              </h2>
              <p className="text-lg text-slate-600" data-id="element-3322">
                How modern health clubs and spas use Offsite+ to deliver
                signature group experiences members can't get anywhere else.
              </p>
            </FadeIn>
          </div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8" data-id="element-3323">
            {[{
            icon: UsersIcon,
            title: 'Members Bring Their Circles',
            desc: 'Members invite their friends, families, or office teams to retreat-style days at your space — branded proposals make it effortless.'
          }, {
            icon: LeafIcon,
            title: 'Turnkey Wellness Programming',
            desc: "Auto-generate full-day agendas combining your facility's modalities — sauna, ice, mobility, breathwork — with licensed wellness science modules."
          }, {
            icon: TrendingUpIcon,
            title: 'Measurable Member Outcomes',
            desc: 'Deliver a post-experience report showing HRV improvement, sleep score deltas, and qualitative wellbeing wins members feel.'
          }].map((item, i) => {
            const Icon = item.icon;
            return <FadeIn key={i} delay={i * 0.1} data-id="element-3324">
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 h-full" data-id="element-3325">
                    <div className="w-12 h-12 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-6" data-id="element-3326">
                      <Icon className="w-6 h-6" data-id="element-3327" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-3328">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-id="element-3329">
                      {item.desc}
                    </p>
                  </div>
                </FadeIn>;
          })}
          </div>
        </Section>

        {/* Wellness Content Partners */}
        <Section className="bg-white" data-id="element-3330">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-3331">
            <FadeIn data-id="element-3332">
              <div className="text-xs font-bold text-brand-teal uppercase tracking-[0.2em] mb-4" data-id="element-3333">
                Sourced from the world's wellness authorities
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4" data-id="element-3334">
                Content from the names members trust.
              </h2>
              <p className="text-lg text-slate-600" data-id="element-3335">
                Every experience is grounded in evidence-based wellness science
                — licensed and curated from leading researchers, clinicians, and
                performance institutes.
              </p>
            </FadeIn>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3336">
            {wellnessPartners.map((partner, i) => <FadeIn key={i} delay={i * 0.04} data-id="element-3337">
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-6 text-center h-full flex flex-col justify-center" data-id="element-3338">
                  <div className="font-bold text-brand-navy text-lg mb-1" data-id="element-3339">
                    {partner.name}
                  </div>
                  <div className="text-xs text-slate-500 font-medium" data-id="element-3340">
                    {partner.category}
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </Section>

        {/* Testimonial */}
        <section className="py-24 px-6 md:px-12 bg-white" data-id="element-3341">
          <div className="max-w-4xl mx-auto" data-id="element-3342">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-brand-navy rounded-[2rem] p-10 md:p-16 text-center relative overflow-hidden" data-id="element-3343">
              <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-brand-teal/10 rounded-full blur-3xl pointer-events-none" data-id="element-3344" />
              <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" data-id="element-3345" />
              <QuoteIcon className="w-12 h-12 text-brand-teal/40 mx-auto mb-8 rotate-180 relative z-10" data-id="element-3346" />
              <p className="text-2xl md:text-3xl font-medium text-white leading-relaxed mb-10 relative z-10" data-id="element-3347">
                "Our members used to come for a 60-minute class. Now they're
                booking full recovery days for their crews — ice plunges,
                mobility work, sound baths, longevity testing. Offsite+ turned
                our facility into a destination."
              </p>
              <div className="relative z-10" data-id="element-3348">
                <div className="font-bold text-brand-teal text-lg" data-id="element-3349">
                  Maya Okonkwo
                </div>
                <div className="text-slate-400" data-id="element-3350">
                  Director of Member Experience, Performance Spa Collective
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <SiteFooter data-id="element-3351" />
    </div>;
}